import { useEffect, useMemo, useState } from 'react';
import type { TuitionMonthlyFee } from '@/types';
import { formatNumber } from '@/lib/datetime';
import { inputClass } from '@/components/base/Field';

export interface MonthlyFeeInput {
  year: number;
  month: number;
  fee: number;
  isExcluded: boolean;
}

export interface MonthlyFeeRef {
  year: number;
  month: number;
}

interface TuitionMonthlyFeePanelProps {
  monthlyFees: TuitionMonthlyFee[];
  baseFee: number;
  onSave: (entries: MonthlyFeeInput[], removed: MonthlyFeeRef[]) => Promise<void>;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export default function TuitionMonthlyFeePanel({
  monthlyFees,
  baseFee,
  onSave,
}: TuitionMonthlyFeePanelProps) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [year, setYear] = useState(currentYear);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [excludedDraft, setExcludedDraft] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const yearFees = useMemo(
    () => monthlyFees.filter((f) => f.year === year),
    [monthlyFees, year],
  );

  useEffect(() => {
    const next: Record<number, string> = {};
    const nextExcluded: Record<number, boolean> = {};
    yearFees.forEach((f) => {
      next[f.month] = f.fee ? String(f.fee) : '';
      nextExcluded[f.month] = f.is_excluded;
    });
    setDraft(next);
    setExcludedDraft(nextExcluded);
  }, [yearFees, year]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  const setValue = (month: number, value: string) => {
    setDraft((prev) => ({ ...prev, [month]: value.replace(/[^0-9]/g, '') }));
  };

  const toggleExcluded = (month: number) => {
    setExcludedDraft((prev) => ({ ...prev, [month]: !prev[month] }));
  };

  const originalFee = (month: number) => {
    const found = yearFees.find((f) => f.month === month);
    return found && found.fee ? String(found.fee) : '';
  };

  const originalExcluded = (month: number) =>
    yearFees.find((f) => f.month === month)?.is_excluded ?? false;

  const dirty = MONTHS.some(
    (m) => (draft[m] ?? '') !== originalFee(m) || Boolean(excludedDraft[m]) !== originalExcluded(m),
  );

  const billedMonths = MONTHS.filter((m) => !excludedDraft[m]);
  const enteredCount = billedMonths.filter((m) => (draft[m] ?? '').trim() !== '').length;
  const excludedCount = MONTHS.filter((m) => excludedDraft[m]).length;
  const yearTotal = billedMonths.reduce((sum, m) => {
    const v = Number(draft[m]);
    return sum + (draft[m] && !Number.isNaN(v) ? v : 0);
  }, 0);

  const save = async () => {
    setErr('');
    const entries: MonthlyFeeInput[] = [];
    const removed: MonthlyFeeRef[] = [];

    for (const m of MONTHS) {
      const raw = (draft[m] ?? '').trim();
      const isExcluded = Boolean(excludedDraft[m]);
      const hasOriginal = yearFees.some((f) => f.month === m);
      const oFee = originalFee(m);
      const oExcluded = originalExcluded(m);

      if (!isExcluded && raw === '') {
        if (hasOriginal) removed.push({ year, month: m });
        continue;
      }
      const amount = raw === '' ? 0 : Number(raw);
      if (Number.isNaN(amount) || amount < 0) {
        setErr(`${m}월 금액을 올바르게 입력해주세요.`);
        return;
      }
      if (!hasOriginal || oFee !== raw || oExcluded !== isExcluded) {
        entries.push({ year, month: m, fee: amount, isExcluded });
      }
    }

    if (entries.length === 0 && removed.length === 0) {
      setNotice('변경된 내용이 없어요.');
      return;
    }

    setBusy(true);
    try {
      await onSave(entries, removed);
      setNotice(`${year}년 월별 교습비를 저장했어요.`);
    } catch {
      setErr('월별 교습비 저장에 실패했어요.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-calendar-line"></i>
            </span>
            월별 교습비
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            달마다 다른 교습비를 직접 입력해요. 입력한 달은 그 금액이, 비워둔 달은 기본 교습비
            {baseFee > 0 ? `(${formatNumber(baseFee)}원)` : ''}가 적용돼요. 휴원·면제로 건너뛸 달은{' '}
            <span className="font-semibold text-foreground-700">회차 제외</span>로 표시하세요.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition hover:bg-background-200"
            aria-label="이전 해"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="min-w-[76px] text-center text-sm font-bold text-foreground-950">
            {year}년
          </span>
          <button
            type="button"
            onClick={() => setYear((y) => y + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition hover:bg-background-200"
            aria-label="다음 해"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>

      {notice && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-100/70 px-3 py-2 text-xs text-primary-700">
          <i className="ri-information-line"></i>
          {notice}
        </div>
      )}
      {err && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {err}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {MONTHS.map((m) => {
          const isCurrent = year === currentYear && m === currentMonth;
          const raw = draft[m] ?? '';
          const isExcluded = Boolean(excludedDraft[m]);
          const hasValue = !isExcluded && raw.trim() !== '';
          return (
            <div
              key={m}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                isExcluded
                  ? 'border-background-300 bg-background-200/70'
                  : isCurrent
                    ? 'border-primary-300 bg-primary-50'
                    : 'border-background-200 bg-background-50'
              }`}
            >
              <span
                className={`w-11 shrink-0 text-xs font-semibold ${
                  isExcluded
                    ? 'text-foreground-400'
                    : isCurrent
                      ? 'text-primary-700'
                      : 'text-foreground-700'
                }`}
              >
                {m}월
              </span>
              {isExcluded ? (
                <span className="flex flex-1 items-center gap-1 text-xs font-semibold text-foreground-500">
                  <i className="ri-forbid-line"></i>
                  회차 제외 (휴원·면제)
                </span>
              ) : (
                <>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={raw}
                    onChange={(e) => setValue(m, e.target.value)}
                    placeholder={baseFee > 0 ? formatNumber(baseFee) : '미입력'}
                    className={`${inputClass} text-right`}
                  />
                  <span className="shrink-0 text-xs text-foreground-500">원</span>
                  {hasValue && (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                      <i className="ri-check-line text-[11px]"></i>
                    </span>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={() => toggleExcluded(m)}
                title={isExcluded ? '제외 해제' : '회차 제외'}
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-xs transition ${
                  isExcluded
                    ? 'border-foreground-300 bg-foreground-700 text-background-50 hover:bg-foreground-800'
                    : 'border-background-300 bg-background-50 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
                }`}
              >
                <i className={isExcluded ? 'ri-arrow-go-back-line' : 'ri-forbid-line'}></i>
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-background-200 px-3 py-1 text-[11px] font-semibold text-foreground-600">
            {year}년 입력 {enteredCount}개월 · 합계 {formatNumber(yearTotal)}원
          </span>
          {excludedCount > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-background-200 px-3 py-1 text-[11px] font-semibold text-foreground-600">
              <i className="ri-forbid-line"></i>
              제외 {excludedCount}개월
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
        >
          {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          변경사항 저장
        </button>
      </div>
    </div>
  );
}